import { ChipMetadata, LinkButton, Tooltip } from '@pmi/dsm-react';
import { DotSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import { useMemo } from 'react';
import { TableCellProps } from '../models';
import styles from './userCelll.module.scss';
import { getTextForNoPersonId } from '../../../../../../shared/helpers';
import usePermissions from '../../../../../../shared/hooks/usePermissions';
import PERMISSIONS from '../../../../../../shared/permissions';

const MAX_EMAIL_CHUNK_LENGTH = 20;

function renderEmail(email: string) {
  if (email.length <= MAX_EMAIL_CHUNK_LENGTH) {
    return email;
  }

  const [local, domain] = email.split('@');
  const chunks: string[] = [];
  let finalChunk = domain;

  for (let i = 0; i < local.length; i += MAX_EMAIL_CHUNK_LENGTH) {
    const txt = local.slice(i, i + MAX_EMAIL_CHUNK_LENGTH);
    if (txt.length < MAX_EMAIL_CHUNK_LENGTH) {
      finalChunk = null;
      chunks.push(`${txt}@${domain}`);
    } else {
      finalChunk = domain;
      chunks.push(txt);
    }
  }

  return (
    <>
      {chunks.map((chunk, i) => (
        <span className="d-block" key={`emailChunk${i.toString()}`}>
          {chunk}
        </span>
      ))}
      {finalChunk && <span className="d-block">@{finalChunk}</span>}
    </>
  );
}

function UserCell({ row, actions }: TableCellProps) {
  const { hasAccess, isLoading: isAccessLoading } = usePermissions();
  const user = row.original;

  const formattedEmail = useMemo(
    () => (user.email ? renderEmail(user.email) : '...'),
    [user.email],
  );

  const isInternalAdmin = useMemo(
    () => hasAccess({ permissions: [PERMISSIONS.ManageElevatedAdmins] }),
    [hasAccess, isAccessLoading],
  );

  const nameOrEmail = user.personName || user?.email;
  const sharedTextClass = `${styles.textContent} d-flex align-items-center gap-1`;

  return (
    <span className={styles.cell}>
      <span className={styles.textContent} data-exclude-lg>
        <LinkButton
          className={styles.linkButton}
          size="sm"
          titleText={nameOrEmail}
          onClick={() => actions.onView(user)}
        />
      </span>
      <span className={styles.textContent} data-exclude-lg>
        <span className={styles.emailBlock}>{formattedEmail}</span>
      </span>
      {isInternalAdmin && (
        <span className={sharedTextClass}>
          <span>PMI ID:</span>
          <span>{user.personId || getTextForNoPersonId(user)}</span>
        </span>
      )}

      {isInternalAdmin && user.hasAcceptedToShareData === false && (
        <span className={sharedTextClass}>
          <span className="small">
            <Tooltip
              type="sm"
              content="DO NOT SHARE product data including exam result, exam date, status, progress and completion with people outside PMI."
              placement="top"
              className="darkTooltip"
            >
              <ChipMetadata
                text="Opted Out"
                icon={<DotSolidIcon />}
                variant="red"
                className={styles.chipDanger}
              />
            </Tooltip>
          </span>
        </span>
      )}
    </span>
  );
}

export default UserCell;
